import { Graphics } from 'pixi.js';
import { Renderer } from '../Renderer';
import { IsoCamera } from '../iso/IsoCamera';
import { InputHandler } from '../iso/InputHandler';
import { OfficeRenderer, type TileDef } from '../office/OfficeRenderer';
import { worldToScreen, TILE_WIDTH, TILE_HEIGHT } from '../iso/IsoGrid';
import { TweenManager } from '../effects/TweenManager';
import { NumberPopupManager } from '../effects/NumberPopup';
import { CharacterSprite } from '../characters/CharacterSprite';
import { SeededRandom } from '../../core/rng/SeededRandom';
import type { GameStore } from '../../store/GameStore';
import type { SimulationState, Staff } from '../../core/types';

// ─── Office Layouts (same as DemoScene, will move to theme data later) ──

const OFFICE_LAYOUTS: { level: number; width: number; height: number; tiles: TileDef[] }[] = [
  {
    level: 1, width: 6, height: 6,
    tiles: [
      { x: 1, y: 1, type: 'desk' }, { x: 2, y: 1, type: 'desk' },
      { x: 1, y: 3, type: 'desk' }, { x: 2, y: 3, type: 'desk' },
      { x: 4, y: 4, type: 'water_cooler' },
      { x: 5, y: 5, type: 'door' },
    ],
  },
  {
    level: 2, width: 8, height: 8,
    tiles: [
      { x: 1, y: 1, type: 'desk' }, { x: 2, y: 1, type: 'desk' }, { x: 3, y: 1, type: 'desk' },
      { x: 1, y: 3, type: 'desk' }, { x: 2, y: 3, type: 'desk' }, { x: 3, y: 3, type: 'desk' },
      { x: 1, y: 5, type: 'desk' }, { x: 2, y: 5, type: 'desk' },
      { x: 6, y: 2, type: 'coffee_machine' }, { x: 6, y: 4, type: 'plant' },
      { x: 5, y: 6, type: 'whiteboard' }, { x: 7, y: 7, type: 'door' },
    ],
  },
  {
    level: 3, width: 12, height: 10,
    tiles: [
      { x: 1, y: 1, type: 'desk' }, { x: 2, y: 1, type: 'desk' }, { x: 3, y: 1, type: 'desk' },
      { x: 1, y: 2, type: 'desk' }, { x: 2, y: 2, type: 'desk' }, { x: 3, y: 2, type: 'desk' },
      { x: 7, y: 1, type: 'desk' }, { x: 8, y: 1, type: 'desk' }, { x: 9, y: 1, type: 'desk' },
      { x: 7, y: 2, type: 'desk' }, { x: 8, y: 2, type: 'desk' }, { x: 9, y: 2, type: 'desk' },
      { x: 1, y: 5, type: 'desk' }, { x: 2, y: 5, type: 'desk' }, { x: 3, y: 5, type: 'desk' },
      { x: 1, y: 6, type: 'desk' }, { x: 2, y: 6, type: 'desk' }, { x: 3, y: 6, type: 'desk' },
      { x: 8, y: 6, type: 'coffee_machine' }, { x: 9, y: 7, type: 'vending_machine' },
      { x: 5, y: 4, type: 'meeting_table' }, { x: 6, y: 3, type: 'whiteboard' },
      { x: 0, y: 0, type: 'plant' }, { x: 11, y: 0, type: 'plant' },
      { x: 10, y: 5, type: 'water_cooler' }, { x: 11, y: 9, type: 'door' },
    ],
  },
  {
    level: 4, width: 16, height: 12,
    tiles: [
      ...Array.from({ length: 4 }, (_, pod) => {
        const ox = (pod % 2) * 8 + 1;
        const oy = Math.floor(pod / 2) * 5 + 1;
        return Array.from({ length: 6 }, (_, i) => ({
          x: ox + (i % 3), y: oy + Math.floor(i / 3), type: 'desk' as const,
        }));
      }).flat(),
      { x: 7, y: 5, type: 'meeting_table' }, { x: 7, y: 3, type: 'whiteboard' },
      { x: 13, y: 9, type: 'coffee_machine' }, { x: 14, y: 9, type: 'vending_machine' },
      { x: 12, y: 10, type: 'plant' }, { x: 13, y: 10, type: 'water_cooler' },
      { x: 14, y: 2, type: 'server_rack' },
      { x: 0, y: 0, type: 'plant_large' }, { x: 15, y: 0, type: 'plant_large' },
      { x: 15, y: 11, type: 'door' },
    ],
  },
  {
    level: 5, width: 20, height: 16,
    tiles: [
      // 6 desk pods (3 cols x 2 rows) — rows at y:1-2 and y:9-10, leaving y:4-7 clear for meeting areas
      ...Array.from({ length: 6 }, (_, pod) => {
        const ox = (pod % 3) * 6 + 1;
        const oy = Math.floor(pod / 3) * 8 + 1; // 8-row spacing between pod rows
        return Array.from({ length: 8 }, (_, i) => ({
          x: ox + (i % 4), y: oy + Math.floor(i / 4), type: 'desk' as const,
        }));
      }).flat(),
      { x: 16, y: 12, type: 'coffee_machine' }, { x: 17, y: 12, type: 'vending_machine' },
      { x: 16, y: 13, type: 'water_cooler' }, { x: 18, y: 13, type: 'plant_large' },
      // Meeting area in the clear zone between desk rows
      { x: 8, y: 5, type: 'meeting_table' }, { x: 14, y: 5, type: 'meeting_table' },
      { x: 8, y: 4, type: 'whiteboard' }, { x: 14, y: 4, type: 'whiteboard' },
      { x: 1, y: 13, type: 'server_rack' }, { x: 2, y: 13, type: 'server_rack' },
      { x: 3, y: 13, type: 'server_rack' },
      { x: 18, y: 6, type: 'arcade' },
      { x: 0, y: 0, type: 'plant_large' }, { x: 19, y: 0, type: 'plant_large' },
      { x: 0, y: 15, type: 'plant_large' },
      { x: 6, y: 0, type: 'plant' }, { x: 12, y: 0, type: 'plant' },
      { x: 19, y: 15, type: 'door' },
    ],
  },
];

/**
 * The real game scene — driven by simulation state via GameStore.
 * Characters map to actual staff, office reflects real level.
 */
export class GameScene {
  private renderer: Renderer;
  private store: GameStore;
  private camera: IsoCamera;
  private input: InputHandler;
  private officeRenderer: OfficeRenderer;
  private tweenManager: TweenManager;
  private characterGraphics: Graphics;

  private characters: Map<string, CharacterSprite> = new Map();
  private hiddenStaffIds: Set<string> = new Set();
  private deskPositions: { x: number; y: number }[] = [];
  private deskAssignments: Map<string, number> = new Map(); // staffId → desk index
  private blockedTiles: Set<string> = new Set();
  private gridWidth: number = 6;
  private gridHeight: number = 6;
  private currentOfficeLevel: number = 0;
  private popups!: NumberPopupManager;
  private popupCooldown: number = 0;
  private needsRecenter: number = 30; // Re-center after renderer has correct size

  constructor(renderer: Renderer, store: GameStore) {
    this.renderer = renderer;
    this.store = store;
    this.officeRenderer = new OfficeRenderer();
    this.tweenManager = new TweenManager();

    this.camera = new IsoCamera(this.officeRenderer.displayObject);
    this.camera.setViewport(renderer.width, renderer.height);

    this.input = new InputHandler(this.camera, renderer.canvas);

    this.characterGraphics = new Graphics();
    this.characterGraphics.zIndex = 2;

    this.popups = new NumberPopupManager(this.officeRenderer.displayObject);

    renderer.stage.addChild(this.officeRenderer.displayObject);
    this.officeRenderer.displayObject.addChild(this.characterGraphics);

    // Render loop
    renderer.addTickerCallback(() => this.update());

    // Initial render from current state
    this.syncOffice();
    this.syncCharacters();
  }

  /** Sync office layout to current office level. */
  private syncOffice(): void {
    const state = this.store.getState();
    const level = state.office.level;

    if (level === this.currentOfficeLevel) return;
    this.currentOfficeLevel = level;

    const layout = OFFICE_LAYOUTS.find((l) => l.level === level) ?? OFFICE_LAYOUTS[0]!;
    this.gridWidth = layout.width;
    this.gridHeight = layout.height;

    this.officeRenderer.render(layout.tiles, layout.width, layout.height, level);

    // Extract desk positions and build blocked set
    this.deskPositions = layout.tiles.filter((t) => t.type === 'desk').map((t) => ({ x: t.x, y: t.y }));
    this.blockedTiles = new Set();
    for (const tile of layout.tiles) {
      this.blockedTiles.add(`${tile.x},${tile.y}`);
    }

    // Camera — center on the middle tile of the grid
    this.camera.setBoundsFromGrid(layout.width, layout.height, TILE_WIDTH, TILE_HEIGHT);
    this.camera.setViewport(this.renderer.width, this.renderer.height);
    const fitZoom = this.camera.fitAll(layout.width, layout.height, TILE_WIDTH, TILE_HEIGHT);
    this.camera.setZoom(fitZoom);
    const gridCenter = worldToScreen(layout.width / 2, layout.height / 2);
    this.camera.jumpTo(gridCenter.x, gridCenter.y);
    document.getElementById('game-canvas')?.classList.add('ready');

    // Clear desk assignments — will be rebuilt in syncCharacters
    this.deskAssignments.clear();
  }

  /** Sync characters to current staff list. */
  private syncCharacters(): void {
    const state = this.store.getState();
    const staffIds = new Set(state.staff.map((s) => s.id));

    // Remove characters for staff that no longer exist
    for (const [id] of this.characters) {
      if (!staffIds.has(id)) {
        this.characters.delete(id);
        this.deskAssignments.delete(id);
      }
    }

    // Track which staff are away (training/leave) — hide but don't delete
    this.hiddenStaffIds.clear();

    // Add/update characters for each staff member
    for (const staff of state.staff) {
      if (staff.isOnLeave || staff.isTraining) {
        this.hiddenStaffIds.add(staff.id);
      }

      let sprite = this.characters.get(staff.id);

      if (!sprite) {
        // New staff — create sprite and assign a desk
        const deskIdx = this.assignDesk(staff.id);
        const desk = deskIdx >= 0 ? this.deskPositions[deskIdx]! : null;

        const rng = new SeededRandom(hashString(staff.id));
        sprite = new CharacterSprite(
          staff.appearance,
          desk?.x ?? 0,
          desk?.y ?? 0,
          rng,
          300,
        );

        if (!desk) {
          // No desk — start idle in a random spot
          sprite.state = 'idle';
          sprite.worldX = Math.floor(this.gridWidth / 2);
          sprite.worldY = Math.floor(this.gridHeight / 2);
        }

        this.characters.set(staff.id, sprite);
      }

      // Update character state based on simulation
      this.updateCharacterState(sprite, staff, state);
    }
  }

  /** Determine if a staff member should be sitting or idle. */
  private updateCharacterState(sprite: CharacterSprite, staff: Staff, state: SimulationState): void {
    if (sprite.state === 'walking') return; // Don't interrupt a walk

    const isWorking = staff.teamId
      ? state.teams.some((t) => t.id === staff.teamId && t.assignedProjectId !== null)
      : false;

    if (isWorking) {
      sprite.state = 'sitting';
    } else if (sprite.state === 'sitting') {
      // Staff stopped working — let them wander
      sprite.state = 'idle';
    }
  }

  /** Assign a desk to a staff member. Returns desk index or -1. */
  private assignDesk(staffId: string): number {
    // Find first unassigned desk
    const assigned = new Set(this.deskAssignments.values());
    for (let i = 0; i < this.deskPositions.length; i++) {
      if (!assigned.has(i)) {
        this.deskAssignments.set(staffId, i);
        return i;
      }
    }
    return -1; // No desks available
  }

  /** Main update — called every frame. */
  private update(): void {
    this.camera.setViewport(this.renderer.width, this.renderer.height);
    this.camera.update();
    this.tweenManager.update(16.67);

    // Re-center camera once renderer has real dimensions (fixes mobile first-load)
    if (this.needsRecenter > 0) {
      this.needsRecenter--;
      if (this.needsRecenter === 0 && this.renderer.width > 0 && this.renderer.height > 0) {
        const fitZoom = this.camera.fitAll(this.gridWidth, this.gridHeight, TILE_WIDTH, TILE_HEIGHT);
        this.camera.setZoom(fitZoom);
        const gridCenter = worldToScreen(this.gridWidth / 2, this.gridHeight / 2);
        this.camera.jumpTo(gridCenter.x, gridCenter.y);
        // Reveal the canvas now that the camera is positioned
        document.getElementById('game-canvas')?.classList.add('ready');
      }
    }

    // Sync with simulation state periodically (not every frame — every 10 frames)
    if (this.renderer.pixiApp.ticker.FPS > 0) {
      const frame = Math.floor(this.renderer.pixiApp.ticker.lastTime / 16);
      if (frame % 10 === 0) {
        this.syncOffice();
        this.syncCharacters();
      }
    }

    // Update and draw characters
    this.characterGraphics.clear();
    const gameSpeed = this.store.getState().speed;
    const isPaused = gameSpeed === 0;

    for (const [id, sprite] of this.characters) {
      if (this.hiddenStaffIds.has(id)) continue;
      // Only update movement/animation when game is running
      // Run multiple updates at higher speeds so characters move faster
      if (!isPaused) {
        const updates = Math.max(1, gameSpeed);
        for (let i = 0; i < updates; i++) {
          sprite.update(this.gridWidth, this.gridHeight, this.blockedTiles, true);
        }
      }
    }

    // Number popups for working staff (only when not paused)
    if (!isPaused) this.popupCooldown--;
    if (!isPaused && this.popupCooldown <= 0) {
      this.popupCooldown = 40; // Every ~40 frames (~0.7s)
      this.spawnWorkPopups();
    }
    this.popups.update();

    // Depth sort and draw (skip hidden staff — training/leave)
    const visible = [...this.characters.entries()]
      .filter(([id]) => !this.hiddenStaffIds.has(id))
      .map(([, sprite]) => sprite)
      .sort((a, b) => a.screenY - b.screenY);
    for (const sprite of visible) {
      sprite.draw(this.characterGraphics);
    }
  }

  /** Spawn number popups above working characters. */
  private spawnWorkPopups(): void {
    const state = this.store.getState();
    const statColors: Record<string, number> = {
      coding: 0x5b9bd5, art: 0xe88ab4, sound: 0xf5d76e, design: 0x4ecca3,
    };
    const statLabels: Record<string, string> = {
      coding: '💻', art: '🎨', sound: '🎵', design: '📐',
    };

    // Pick one random working staff to show a popup for
    const workingStaff = state.staff.filter((s) => {
      if (s.isTraining || s.isOnLeave) return false;
      return s.teamId ? state.teams.some((t) => t.id === s.teamId && t.assignedProjectId) : false;
    });

    if (workingStaff.length === 0) return;

    // Pick a random one
    const rng = new SeededRandom(state.company.currentTick);
    const staff = workingStaff[rng.nextInt(0, workingStaff.length - 1)]!;
    const sprite = this.characters.get(staff.id);
    if (!sprite) return;

    // Pick a random stat
    const stats = Object.keys(staff.stats);
    if (stats.length === 0) return;
    const stat = stats[rng.nextInt(0, stats.length - 1)]!;
    const value = Math.max(1, Math.round((staff.stats[stat] ?? 5) * 0.1));

    const screen = worldToScreen(sprite.worldX, sprite.worldY);
    const label = statLabels[stat] ?? stat;
    const color = statColors[stat] ?? 0xffffff;

    this.popups.spawn(screen.x, screen.y - 20, `+${value} ${label}`, color);
  }

  /** Burst of celebratory popups across the office. */
  celebrate(message: string): void {
    const colors = [0xf5d76e, 0x4ecca3, 0xe88ab4, 0x5b9bd5, 0xffffff];
    const emojis = ['⭐', '🎉', '🏆', '✨', '🎊'];
    for (let i = 0; i < 6; i++) {
      const x = 100 + Math.random() * (this.gridWidth * TILE_WIDTH * 0.6);
      const y = 50 + Math.random() * (this.gridHeight * TILE_HEIGHT * 0.6);
      const color = colors[i % colors.length]!;
      const emoji = emojis[i % emojis.length]!;
      setTimeout(() => {
        this.popups.spawn(x, y, i === 0 ? message : emoji, color);
      }, i * 150);
    }
  }

  destroy(): void {
    this.input.destroy();
    this.popups.destroy();
  }
}

/** Simple string hash for deterministic RNG seeding per staff member. */
function hashString(s: string): number {
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    const ch = s.charCodeAt(i);
    hash = ((hash << 5) - hash) + ch;
    hash |= 0;
  }
  return Math.abs(hash);
}
